import React, { memo, useMemo, useCallback } from 'react';
import MultiSelectDropdown from '../../../Common/MultiSelectDropdown';

const ModelingSection = memo(({
  formData,
  setFormData,
  isEditing = true,
  modelOptions,
  scalerOptions,
  metadataFields,
  distinctValues,
}) => {
  // Get the selected attribute from Training section
  const selectedAttribute = formData.attribute;

  // Get distinct values for the selected attribute from Training section
  const getAttributeDistinctValues = useCallback(() => {
    if (!distinctValues || !selectedAttribute) return [];
    const values = distinctValues[selectedAttribute] || [];
    return values.map(value => ({ value, label: String(value) }));
  }, [distinctValues, selectedAttribute]);

  // Handle case value selection
  const handleCaseValueChange = useCallback((selectedValues) => {
    setFormData({
      ...formData,
      modeling_type: {
        ...formData.modeling_type,
        case: selectedValues,
        control: (formData.modeling_type?.control || []).filter(value => !selectedValues.includes(value))
      }
    });
  }, [formData, setFormData]);

  // Handle control value selection
  const handleControlValueChange = useCallback((selectedValues) => {
    setFormData({
      ...formData,
      modeling_type: {
        ...formData.modeling_type,
        control: selectedValues,
        case: (formData.modeling_type?.case || []).filter(value => !selectedValues.includes(value))
      }
    });
  }, [formData, setFormData]);

  // Handle multi-class classes selection
  const handleClassesChange = useCallback((selectedValues) => {
    setFormData({
      ...formData,
      modeling_type: {
        ...formData.modeling_type,
        classes: selectedValues
      }
    });
  }, [formData, setFormData]);

  // Get the currently selected case and control values
  const selectedCaseValues = formData.modeling_type?.case || [];
  const selectedControlValues = formData.modeling_type?.control || [];

  // Get distinct values for the selected attribute
  const attributeDistinctValues = useMemo(() =>
    getAttributeDistinctValues(),
    [getAttributeDistinctValues]
  );

  // Get available values for case (all values except selected control values)
  const availableCaseValues = useMemo(() => {
    return attributeDistinctValues.filter(valueObj =>
      !selectedControlValues.includes(valueObj.value)
    );
  }, [attributeDistinctValues, selectedControlValues]);

  // Get available values for control (all values except selected case values)
  const availableControlValues = useMemo(() => {
    return attributeDistinctValues.filter(valueObj =>
      !selectedCaseValues.includes(valueObj.value)
    );
  }, [attributeDistinctValues, selectedCaseValues]);
  return (
    <div className="bg-white rounded-lg shadow p-4 lg:p-6">
      <h2 className="text-lg font-semibold mb-4">Modeling</h2>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Problem Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Problem</label>
          <div className="relative">
            <select
              className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.modeling_type?.name || 'classification'}
              onChange={(e) => setFormData({
                ...formData,
                modeling_type: { ...(formData.modeling_type || {}), name: e.target.value },
                evaluation_metrics: [] // Reset evaluation metrics when problem type changes
              })}
              disabled={!isEditing}
            >
              <option value="classification">Classification</option>
              <option value="regression">Regression</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Classification Type */}
        {formData.modeling_type?.name === 'classification' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <div className="relative">
              <select
                className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                value={formData.modeling_type?.type || 'binary'}
                onChange={(e) => setFormData({
                  ...formData,
                  modeling_type: { ...(formData.modeling_type || {}), type: e.target.value },
                  evaluation_metrics: [] // Reset evaluation metrics when classification type changes
                })}
                disabled={!isEditing}
              >
                <option value="binary">Binary</option>
                <option value="multi-class">Multi-class</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        )}

        {/* Case/Control for Binary */}
        {formData.modeling_type?.name === 'classification' && formData.modeling_type?.type === 'binary' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Case</label>
              <MultiSelectDropdown
                options={availableCaseValues}
                selectedValues={selectedCaseValues}
                onChange={handleCaseValueChange}
                placeholder={selectedAttribute ? "Select case values..." : "Select attribute in Training section first"}
                disabled={!isEditing || !selectedAttribute}
                isEditing={isEditing}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Control</label>
              <MultiSelectDropdown
                options={availableControlValues}
                selectedValues={selectedControlValues}
                onChange={handleControlValueChange}
                placeholder={selectedAttribute ? "Select control values..." : "Select attribute in Training section first"}
                disabled={!isEditing || !selectedAttribute}
                isEditing={isEditing}
              />
            </div>
          </>
        )}

        {/* Classes for Multi-class */}
        {formData.modeling_type?.name === 'classification' && formData.modeling_type?.type === 'multi-class' && (
          <div className="lg:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">Classes</label>
            <MultiSelectDropdown
              options={attributeDistinctValues}
              selectedValues={formData.modeling_type?.classes || []}
              onChange={handleClassesChange}
              placeholder={selectedAttribute ? "Select class values..." : "Select attribute in Training section first"}
              disabled={!isEditing || !selectedAttribute}
              isEditing={isEditing}
            />
          </div>
        )}

        {/* Model */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Model</label>
          <div className="relative">
            <select
              className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.model}
              onChange={(e) => setFormData({...formData, model: e.target.value})}
              disabled={!isEditing}
            >
              {modelOptions[formData.modeling_type?.name || 'classification']?.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {/* Normalize */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Normalize</label>
          <div className="relative">
            <select
              className="w-full border rounded px-3 py-2 appearance-none bg-white pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              value={formData.normalize}
              onChange={(e) => setFormData({...formData, normalize: e.target.value})}
              disabled={!isEditing}
            >
              {scalerOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
              <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

ModelingSection.displayName = 'ModelingSection';

export default ModelingSection;
